/* ============================================================================
   TARIFARIOS-UI · reglas de presentación de la pantalla de Tarifarios.

   Están aquí, fuera de los componentes, porque son las tres cosas de esta
   pantalla que hay que poder comprobar sin montar React: qué significa
   "sincronizado", cuánto cambió un precio, y qué campos se han tocado de
   verdad.

   NADA de esto escribe en la base ni cambia lo que se envía a la API. El
   valor guardado viaja intacto: `tituloClinico` sólo pinta (ver
   utils/textoClinico.js), y las constantes de alcance son texto de interfaz.
   ========================================================================== */

/**
 * Sincronizado con Contífico = tiene `contifico_id`.
 *
 * Se deduce del ID, no se inventa: el backend NO tiene ningún campo de estado
 * de sincronización (ver `ExamenTipo` en schema.prisma -- codigo, descripcion,
 * estado, tipo, categoria_id, contifico_id, y ya). `contifico_id` es
 * `String? @db.Text`, así que puede llegar nulo, ausente O como cadena vacía;
 * los tres significan lo mismo y por eso se comprueba el contenido, no sólo
 * la nulidad.
 *
 * OJO con dónde vive: `contifico_id` está en `ExamenTipo`, que es catálogo
 * GLOBAL. Un examen sin sincronizar lo está en las 19 entidades a la vez --
 * no es una propiedad de este tarifario.
 */
export function estaSincronizado(contificoId) {
    if (typeof contificoId !== "string") return false;
    return contificoId.trim() !== "";
}

/**
 * Iniciales para el avatar de la entidad: "Asisken" -> "AS",
 * "SEGUROS DEL PICHINCHA" -> "SP". Máximo dos letras, siempre en mayúscula.
 */
export function inicialesEntidad(nombre) {
    if (typeof nombre !== "string") return "";
    const palabras = nombre.trim().split(/\s+/).filter(Boolean);
    if (palabras.length === 0) return "";
    if (palabras.length === 1) {
        return palabras[0].slice(0, 2).toLocaleUpperCase("es");
    }
    return (palabras[0][0] + palabras[1][0]).toLocaleUpperCase("es");
}

/** `20` -> `"$20.00"`. Acepta el Decimal serializado como string por Prisma. */
export function formatearPrecio(valor) {
    const n = Number(valor);
    return `$${(Number.isFinite(n) ? n : 0).toFixed(2)}`;
}

/**
 * Cambio de precio, para poder enseñarlo antes de guardar.
 *
 * Devuelve `null` cuando NO hay nada que enseñar: valores no numéricos, o un
 * precio que no ha cambiado. Así el componente sólo tiene que preguntar si hay
 * variación, sin repetir la comparación.
 *
 * `porcentaje` es `null` si el precio anterior era 0: "subió un ∞%" no
 * significa nada y es mejor no decir nada que decir una cifra falsa.
 */
export function variacionPrecio(anterior, nuevo) {
    // `Number("")` es 0, no NaN: sin esta guarda, borrar el campo de precio
    // pintaría "$20.00 -> $0.00 · -100%" mientras el usuario está escribiendo.
    // Un campo vacío no es un precio de cero, es un campo sin rellenar.
    const vacio = (v) => v === null || v === undefined || String(v).trim() === "";
    if (vacio(anterior) || vacio(nuevo)) return null;
    const a = Number(anterior);
    const n = Number(nuevo);
    if (!Number.isFinite(a) || !Number.isFinite(n)) return null;
    // Se compara con dos decimales, que es la precisión real de la columna
    // (`Decimal(10,2)`): 20 y 20.001 son el mismo precio para este sistema.
    if (a.toFixed(2) === n.toFixed(2)) return null;
    const porcentaje = a > 0 ? ((n - a) / a) * 100 : null;
    return {
        anterior: a,
        nuevo: n,
        porcentaje,
        subio: n > a,
    };
}

/** `10` -> `"+10%"`; `-5.5` -> `"-5.5%"`. Sin decimales si son redondos. */
export function formatearPorcentaje(porcentaje) {
    if (!Number.isFinite(porcentaje)) return "";
    const redondeado = Math.round(porcentaje * 10) / 10;
    const texto = Number.isInteger(redondeado) ? String(redondeado) : redondeado.toFixed(1);
    return `${redondeado > 0 ? "+" : ""}${texto}%`;
}

/* -- qué se ha tocado de verdad ------------------------------------------- */

/**
 * Campos que el usuario puede modificar. El orden importa: es el orden en el
 * que se leen en el formulario, y por tanto el de cualquier resumen.
 */
export const CAMPOS_EDITABLES = ["descripcion", "categoria_id", "tipo", "seguro_id", "precio"];

const mismoValor = (a, b) => {
    // El formulario mezcla strings del `<input>` con números de la API; sin
    // esto, escribir "20" sobre un precio de 20 contaría como un cambio y
    // "Guardar cambios" se habilitaría sin que nada haya cambiado.
    if (a === null || a === undefined || a === "") return b === null || b === undefined || b === "";
    if (b === null || b === undefined || b === "") return false;
    const na = Number(a);
    const nb = Number(b);
    if (Number.isFinite(na) && Number.isFinite(nb)) return na === nb;
    return String(a).trim() === String(b).trim();
};

/** Lista de campos cuyo valor difiere entre el original y el formulario. */
export function camposCambiados(original, actual) {
    if (!original || !actual) return [];
    return CAMPOS_EDITABLES.filter((campo) => !mismoValor(original[campo], actual[campo]));
}

/* -- textos de alcance ----------------------------------------------------
   Los dos salen de la auditoría de FASE 0 y están VERIFICADOS contra el
   backend, no supuestos:

   · El precio: `facturas_detalle.valor` es una foto congelada al facturar
     (FBilling.ts), así que lo emitido no cambia nunca. PERO
     ControllerBilling.ts recalcula el reparto paciente/aseguradora leyendo
     `planes_examenes.tarifario.precio` EN VIVO al facturar, así que una
     prestación pendiente cubierta por un plan sí cambia de precio.

   · El catálogo: descripción, categoría y tipo viven en `ExamenTipo`, que NO
     está scopeado por entidad. ControllerActualizarProcedimientoPrecios los
     escribe ahí, así que cambian para las 19 aseguradoras a la vez. Sólo
     `precio` es de este tarifario.
   ------------------------------------------------------------------------- */

export const ALCANCE_PRECIO =
    "El nuevo precio aplica a las facturas que se emitan desde ahora. Las ya emitidas no cambian. " +
    "Ojo: las prestaciones pendientes de cobrar que estén cubiertas por un plan se recalcularán con " +
    "este precio al facturarlas.";

export const ALCANCE_CATALOGO =
    "La descripción, la categoría y el tipo son del procedimiento, no de este tarifario: al cambiarlos, " +
    "cambian también en las demás entidades.";

/** Campos del formulario que pertenecen al catálogo global, no al tarifario. */
export const CAMPOS_DE_CATALOGO = ["descripcion", "categoria_id", "tipo"];

/** ¿El cambio en curso toca el catálogo global? Decide si se avisa o no. */
export function tocaElCatalogo(cambiados) {
    return (cambiados || []).some((campo) => CAMPOS_DE_CATALOGO.includes(campo));
}
